import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from "chart.js";
import { ChevronLeft } from "lucide-react";
import { DateRangePicker, Separator } from "@racoon/ui";
import { Namespace, useDateFnsLocale, useT } from "@racoon/i18n";
import { useGetGovGeoAccessQuery } from "~/shared/api/govDash/govDash.api";
import { useGetDailySummaryListQuery } from "~/shared/api/daily-summary/daily-summary.api";
import { useDriverOnShiftsInRange } from "~/shared/hooks/use-active-truck-count.hook";
import type {
  AnalyticsDetailNavState,
  AnalyticsFilterValue,
  MvoAnalyticsType,
} from "./mvo-analytics-section.types";
import { DropdownSelector } from "~/ui/components/dropdownSelector";
import {
  buildChartConfig,
  buildChartLegend,
  orderedRowsFromSummary,
  PAGE_TITLES,
} from "./analytics-report.shared";
import {
  normalizeDateRange,
  parseLocalDateParam,
  startOfLocalDay,
  toDateParam,
} from "~/shared/utils/filterDateFormatter";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

type FilterValue = AnalyticsFilterValue;

const ANALYTICS_TYPES: MvoAnalyticsType[] = [
  "stands",
  "volumes",
  "containers",
  "equipment",
  "recycling",
];

const isValidAnalyticsType = (value?: string): value is MvoAnalyticsType =>
  !!value && ANALYTICS_TYPES.includes(value as MvoAnalyticsType);

export function MvoAnalyticsSectionDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { analyticsType: analyticsTypeParam } = useParams();
  const t = useT();
  const dateFnsLocale = useDateFnsLocale();
  const navState = (location.state ?? null) as AnalyticsDetailNavState | null;

  const validType = isValidAnalyticsType(analyticsTypeParam);
  const canEnter = validType && !!navState?.mvoId;

  // Dropdown states
  const [selectedRegion, setSelectedRegion] = useState<FilterValue>(() => ({
    label: t(Namespace.Govdash, "allDistricts") || "Все районы",
    value: "all",
  }));
  const [selectedMvo, setSelectedMvo] = useState<FilterValue>(() => ({
    label: navState?.mvoName ?? "",
    value: navState?.mvoId ?? "all",
  }));
  const { data: { govGeoAccesses: geoAccessData } = {} } = useGetGovGeoAccessQuery({});
  const serviceProviders = useMemo(() => {
    if (!geoAccessData) return [];
    if (selectedRegion.value === "all") {
      const allProviders = geoAccessData.flatMap((geo) => geo.serviceProviders || []);
      return Array.from(new Set(allProviders.map((p) => p.id))).map((id) =>
        allProviders.find((p) => p.id === id),
      );
    }
    const region = geoAccessData.find((g) => g.id.toString() === selectedRegion.value);
    return region?.serviceProviders || [];
  }, [geoAccessData, selectedRegion]);

  const mvoOptions = useMemo(
    () =>
      (serviceProviders ?? []).map((provider: any) => ({
        value: provider.id.toString(),
        label: provider.name,
      })),
    [serviceProviders],
  );

  const initialRange = useMemo(() => {
    const defaultEnd = startOfLocalDay(new Date());
    const defaultStart = new Date(defaultEnd);
    defaultStart.setDate(defaultStart.getDate() - 7);
    const navStart = parseLocalDateParam(navState?.startDate);
    const navEnd = parseLocalDateParam(navState?.endDate);

    if (navState?.isSingleDay && navEnd) {
      const rangeEnd = navEnd;
      const rangeStart = new Date(navEnd);
      rangeStart.setDate(rangeStart.getDate() - 7);
      return normalizeDateRange({ from: rangeStart, to: rangeEnd });
    }

    return normalizeDateRange({
      from: navStart ?? defaultStart,
      to: navEnd ?? defaultEnd,
    });
  }, [navState?.endDate, navState?.isSingleDay, navState?.startDate]);

  const [rangeStart, setRangeStart] = useState<Date>(initialRange.from!);
  const [rangeEnd, setRangeEnd] = useState<Date>(initialRange.to!);

  useEffect(() => {
    if (!geoAccessData?.length || !navState?.mvoId) return;
    const region = geoAccessData.find((g) =>
      g.serviceProviders?.some((p) => p.id.toString() === navState.mvoId),
    );
    if (region) {
      setSelectedRegion({ label: region.areaName, value: region.id.toString() });
    }
  }, [geoAccessData, navState?.mvoId]);

  const { data: summaryData } = useGetDailySummaryListQuery(
    {
      owner_organization_id: selectedMvo.value !== "all" ? selectedMvo.value : undefined,
      date_from: toDateParam(rangeStart),
      date_to: toDateParam(rangeEnd),
    },
    { skip: !canEnter },
  );

  const orderedRows = useMemo(() => orderedRowsFromSummary(summaryData), [summaryData]);

  const totalTrucksBaseline = navState?.totalTrucks ?? 0;

  const selectedMvoIds = useMemo(() => [selectedMvo.value], [selectedMvo.value]);
  const { shifts: driverOnShifts } = useDriverOnShiftsInRange({
    serviceProviderIds: selectedMvoIds,
    from: rangeStart,
    to: rangeEnd,
    skip: !canEnter || analyticsTypeParam !== "equipment" || selectedMvo.value === "all",
  });

  const chartConfig = useMemo(() => {
    if (!validType || !analyticsTypeParam) {
      return { data: { labels: [], datasets: [] }, options: {} };
    }
    return buildChartConfig({
      analyticsType: analyticsTypeParam as MvoAnalyticsType,
      rangeStart,
      rangeEnd,
      orderedRows,
      totalTrucksBaseline,
      // Keep the chart empty rather than falling back to summary counts while shifts load.
      driverOnShifts: driverOnShifts ?? [],
      dateLocale: dateFnsLocale,
      t,
    });
  }, [
    analyticsTypeParam,
    dateFnsLocale,
    driverOnShifts,
    orderedRows,
    rangeEnd,
    rangeStart,
    totalTrucksBaseline,
    validType,
    t,
  ]);

  const legend = useMemo(() => {
    if (!validType || !analyticsTypeParam) return [];
    return buildChartLegend(analyticsTypeParam as MvoAnalyticsType, orderedRows, t);
  }, [analyticsTypeParam, orderedRows, validType, t]);

  const handleRangeUpdate = useCallback((values: any) => {
    const from = (values?.range?.from ?? values?.from) as Date | undefined;
    const to = (values?.range?.to ?? values?.to ?? from) as Date | undefined;
    const nextRange = normalizeDateRange({
      from,
      to,
    });
    if (nextRange.from && nextRange.to) {
      setRangeStart(nextRange.from);
      setRangeEnd(nextRange.to);
    }
  }, []);

  if (!validType || !navState?.mvoId) {
    return <Navigate to="/analytics" replace />;
  }

  const analyticsType = analyticsTypeParam as MvoAnalyticsType;

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-[#f2f4f7]">
      <div className="h-11 border-b border-gray-200 flex items-center px-3 bg-white">
        <button
          type="button"
          onClick={() => navigate("/analytics", { state: navState?.returnState })}
          className="size-8 flex items-center justify-center rounded-lg hover:bg-gray-100"
          aria-label="Назад"
        >
          <ChevronLeft className="size-5" />
        </button>
        <h2 className="flex-1 text-center text-base font-semibold text-gray-900 tracking-tight pr-8">
          {PAGE_TITLES(t)[analyticsType]}
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="flex flex-col gap-2 items-start justify-center p-4 w-full">
            <DropdownSelector
              triggerLabel={selectedMvo.label}
              value={selectedMvo.value}
              options={mvoOptions}
              allOptionLabel={t(Namespace.Govdash, "allMvos") || "Все МВО"}
              onApply={(value) => {
                if (value === "all") {
                  setSelectedMvo({
                    label: t(Namespace.Govdash, "allMvos") || "Все МВО",
                    value: "all",
                  });
                  return;
                }
                const option = mvoOptions.find((opt) => opt.value === value);
                if (option) setSelectedMvo(option);
              }}
              menuClassName="max-h-60 overflow-y-auto"
              placeholderTitle={t(Namespace.Govdash, "selectMvoPlaceholder") || "Выберите МВО"}
            />
          </div>

          <Separator className="w-full" />

          <div className="p-4 flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-gray-900">
              {t(Namespace.Govdash, "forPeriod")}
            </span>
            <div className="min-w-0">
              <DateRangePicker
                initialDateFrom={rangeStart}
                initialDateTo={rangeEnd}
                onUpdate={handleRangeUpdate}
                disableFutureDates={true}
                showPresets={false}
              />
            </div>
          </div>

          <div className="px-4 pb-2 flex items-center justify-center gap-4 flex-wrap">
            {legend.map((entry) => (
              <div key={entry.label} className="flex items-center gap-1">
                <div className={`size-3 rounded-full ${entry.color}`} />
                <span className="text-xs font-medium text-gray-900">{entry.label}</span>
              </div>
            ))}
          </div>

          <div className="px-4 pb-2">
            <div className="h-[220px] w-full">
              {orderedRows.length > 0 ? (
                <Line data={chartConfig.data as any} options={chartConfig.options as any} />
              ) : (
                <div className="h-full flex items-center justify-center border border-dashed border-gray-200 rounded-lg text-sm text-gray-500">
                  {t(Namespace.Govdash, "analytics.noDataForSelectedPeriod") ||
                    "Нет данных за выбранный период"}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
